import { useState } from "react";
import { Input } from "../ui/input";
import { Switch } from "../ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import Pagination from "./Pagination";
import { STATUS_CHIP } from "./constants";
import ConfirmModal from "../ConfirmModal";
import { Search, Settings2, Unplug, Save, Loader2 } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";

function daysSince(isoDate) {
  if (!isoDate) return "—";
  const diff = Date.now() - new Date(isoDate).getTime();
  const days = Math.floor(diff / 86400000);
  return days < 0 ? "—" : days;
}

export default function ChipsMonitor({ chips, metrics, search, onSearchChange, onSearchSubmit, status, onStatusChange, onPage, onRefresh }) {
  const [togglingWarmup, setTogglingWarmup] = useState({});
  const [delayEdit, setDelayEdit] = useState(null);
  const [savingDelay, setSavingDelay] = useState(false);
  const [disconnectTarget, setDisconnectTarget] = useState(null);
  const [disconnecting, setDisconnecting] = useState(false);

  const toggleWarmup = async (chip) => {
    setTogglingWarmup((p) => ({ ...p, [chip.id]: true }));
    try {
      await api.patch(`/admin/chips/${chip.id}`, { warmup_enabled: !chip.warmup_enabled });
      toast.success(`Aquecimento ${chip.warmup_enabled ? "pausado" : "retomado"}: ${chip.apelido}`);
      onRefresh?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao alterar aquecimento");
    } finally {
      setTogglingWarmup((p) => ({ ...p, [chip.id]: false }));
    }
  };

  const openDelay = (chip) => {
    setDelayEdit({ chipId: chip.id, apelido: chip.apelido, delay_min: chip.delay_min ?? 45, delay_max: chip.delay_max ?? 90 });
  };

  const saveDelay = async () => {
    if (!delayEdit) return;
    if (delayEdit.delay_max < delayEdit.delay_min) {
      toast.error("O delay máximo deve ser maior ou igual ao mínimo");
      return;
    }
    setSavingDelay(true);
    try {
      await api.patch(`/admin/chips/${delayEdit.chipId}`, {
        delay_min: delayEdit.delay_min,
        delay_max: delayEdit.delay_max,
      });
      toast.success(`Delays atualizados: ${delayEdit.apelido}`);
      setDelayEdit(null);
      onRefresh?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao salvar delays");
    } finally {
      setSavingDelay(false);
    }
  };

  const confirmDisconnect = async () => {
    if (!disconnectTarget) return;
    setDisconnecting(true);
    try {
      await api.patch(`/admin/chips/${disconnectTarget.id}`, { disconnect: true });
      toast.success(`Sessão encerrada: ${disconnectTarget.apelido}`);
      setDisconnectTarget(null);
      onRefresh?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao encerrar sessão");
    } finally {
      setDisconnecting(false);
    }
  };

  return (
    <div className="glass-card overflow-hidden" data-testid="admin-chips-monitor">
      <div className="section-accent p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="font-heading text-lg font-semibold text-slate-100 flex items-center gap-2">
            Monitor de Chips — Conexão Evolution
            {metrics && metrics.disconnected_chips > 0 && (
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-500/20 text-red-300 border border-red-500/40 animate-pulse-dot" data-testid="admin-chips-alert">
                {metrics.disconnected_chips} CAÍDO(S)
              </span>
            )}
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">Todos os chips da base. Busque por apelido/telefone e filtre sessões caídas.</p>
        </div>
        <div className="flex gap-2 w-full sm:w-auto">
          <div className="relative flex-1 sm:w-56">
            <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input value={search} onChange={(e) => onSearchChange(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && onSearchSubmit()}
              placeholder="Apelido ou telefone..."
              data-testid="admin-chip-search"
              className="bg-slate-800/60 border-slate-700 text-slate-100 placeholder:text-slate-500 pl-9 h-9 text-sm" />
          </div>
          <Select value={status} onValueChange={onStatusChange}>
            <SelectTrigger data-testid="admin-chip-status-filter" className="bg-slate-800/60 border-slate-700 text-slate-100 h-9 w-40 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
              <SelectItem value="todos">Todos os status</SelectItem>
              <SelectItem value="conectado">Conectado</SelectItem>
              <SelectItem value="aguardando_qr">Aguardando QR</SelectItem>
              <SelectItem value="desconectado">Sessão caiu</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm table-zebra">
          <thead>
            <tr className="border-b border-white/[0.06]">
              <th className="text-left text-xs font-semibold uppercase tracking-wider text-slate-400 px-5 py-3">Chip</th>
              <th className="text-left text-xs font-semibold uppercase tracking-wider text-slate-400 px-5 py-3">Cliente</th>
              <th className="text-left text-xs font-semibold uppercase tracking-wider text-slate-400 px-5 py-3">Número</th>
              <th className="text-left text-xs font-semibold uppercase tracking-wider text-slate-400 px-5 py-3">Modo</th>
              <th className="text-left text-xs font-semibold uppercase tracking-wider text-slate-400 px-5 py-3">Status</th>
              <th className="text-right text-xs font-semibold uppercase tracking-wider text-slate-400 px-5 py-3">Msgs</th>
              <th className="text-center text-xs font-semibold uppercase tracking-wider text-slate-400 px-5 py-3">Dias</th>
              <th className="text-center text-xs font-semibold uppercase tracking-wider text-slate-400 px-5 py-3">Aquecimento</th>
              <th className="text-center text-xs font-semibold uppercase tracking-wider text-slate-400 px-5 py-3">Ações</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/[0.04]">
            {chips.items.length === 0 && (
              <tr><td colSpan={9} className="px-5 py-8 text-center text-sm text-slate-500" data-testid="admin-chips-empty">Nenhum chip encontrado.</td></tr>
            )}
            {chips.items.map((c) => {
              const st = STATUS_CHIP[c.status] || STATUS_CHIP.aguardando_qr;
              return (
                <tr key={c.id} data-testid={`admin-chip-row-${c.id}`} className={c.status === "desconectado" ? "bg-red-500/[0.04]" : ""}>
                  <td className="px-5 py-3.5 font-medium text-slate-200">{c.apelido}</td>
                  <td className="px-5 py-3.5 text-xs text-slate-400">{c.owner_email || "—"}</td>
                  <td className="px-5 py-3.5 font-mono text-xs text-slate-300">{c.number || "—"}</td>
                  <td className="px-5 py-3.5">
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${c.wa_mode === "real" ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" : "bg-slate-800 text-slate-400 border-slate-700"}`}>
                      {c.wa_mode === "real" ? "REAL" : "SIM"}
                    </span>
                  </td>
                  <td className="px-5 py-3.5">
                    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full uppercase tracking-wider border ${st.cls}`}>{st.label}</span>
                  </td>
                  <td className="px-5 py-3.5 text-right font-mono text-slate-300">{c.total_messages || 0}</td>
                  <td className="px-5 py-3.5 text-center text-slate-300 font-mono text-xs">{daysSince(c.connected_at)}</td>
                  <td className="px-5 py-3.5 text-center">
                    <Switch
                      checked={!!c.warmup_enabled}
                      disabled={!!togglingWarmup[c.id] || c.status !== "conectado"}
                      onCheckedChange={() => toggleWarmup(c)}
                    />
                  </td>
                  <td className="px-5 py-3.5 text-center">
                    <div className="flex items-center justify-center gap-1">
                      <button
                        onClick={() => openDelay(c)}
                        title="Editar delays"
                        className="p-1.5 rounded-md hover:bg-slate-700/60 text-slate-400 hover:text-slate-200 transition-colors"
                      >
                        <Settings2 className="w-4 h-4" />
                      </button>
                      {c.status === "conectado" && (
                        <button
                          onClick={() => setDisconnectTarget(c)}
                          title="Encerrar sessão do chip"
                          className="p-1.5 rounded-md hover:bg-red-500/20 text-slate-400 hover:text-red-300 transition-colors"
                        >
                          <Unplug className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <Pagination page={chips.page} pages={chips.pages} total={chips.total} onPage={onPage} testid="admin-chips-pagination" />

      {/* Delay edit modal */}
      {delayEdit && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={() => !savingDelay && setDelayEdit(null)}>
          <div className="bg-slate-900 border border-slate-700 rounded-xl p-6 w-full max-w-sm shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-base font-semibold text-slate-100 mb-1">Editar Delays</h3>
            <p className="text-xs text-slate-500 mb-4">Chip: {delayEdit.apelido}</p>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-slate-400 mb-1 block">Delay Mínimo (s)</label>
                <Input
                  type="number" min={10} max={3600}
                  value={delayEdit.delay_min}
                  onChange={(e) => setDelayEdit((p) => ({ ...p, delay_min: Number(e.target.value) }))}
                  className="bg-slate-800/60 border-slate-700 text-slate-100 h-9 text-sm"
                />
              </div>
              <div>
                <label className="text-xs text-slate-400 mb-1 block">Delay Máximo (s)</label>
                <Input
                  type="number" min={10} max={7200}
                  value={delayEdit.delay_max}
                  onChange={(e) => setDelayEdit((p) => ({ ...p, delay_max: Number(e.target.value) }))}
                  className="bg-slate-800/60 border-slate-700 text-slate-100 h-9 text-sm"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button
                onClick={() => setDelayEdit(null)}
                disabled={savingDelay}
                className="px-3 py-1.5 text-xs rounded-lg border border-slate-700 text-slate-400 hover:text-slate-200 hover:bg-slate-800 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={saveDelay}
                disabled={savingDelay}
                className="px-3 py-1.5 text-xs rounded-lg bg-brand-500 hover:bg-brand-600 text-white font-semibold flex items-center gap-1.5 transition-colors disabled:opacity-50"
              >
                {savingDelay ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                Salvar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Disconnect confirm modal */}
      <ConfirmModal
        open={!!disconnectTarget}
        onClose={() => setDisconnectTarget(null)}
        onConfirm={confirmDisconnect}
        title="Encerrar sessão do chip"
        message={disconnectTarget ? `Tem certeza que deseja encerrar a sessão WhatsApp do chip "${disconnectTarget.apelido}"? O chip permanece cadastrado e a instância é mantida — para reconectar, gere um novo QR Code no dashboard.` : ""}
        confirmLabel="Encerrar sessão"
        danger
        loading={disconnecting}
      />
    </div>
  );
}